import React, { useState } from 'react';
import FrequentPeopleModal from '../FrequentPeopleModal';

interface SplitPerson {
    person_name: string;
    person_email?: string;
}

interface SplitPeopleStepProps {
    splitPeople: SplitPerson[];
    setSplitPeople: (people: SplitPerson[]) => void;
    isSplitProcessing: boolean;
    splitStatusMessage: string;
    errorMessage: string;
    onSavePeople: () => void;
    onBackFromSplit: () => void;
    getAuthToken: () => Promise<string | null>;
}

const SplitPeopleStep: React.FC<SplitPeopleStepProps> = ({
    splitPeople,
    setSplitPeople,
    isSplitProcessing,
    splitStatusMessage,
    errorMessage,
    onSavePeople,
    onBackFromSplit,
    getAuthToken
}) => {
    const [showFrequentPeopleModal, setShowFrequentPeopleModal] = useState(false);
    const [selectedPersonIndex, setSelectedPersonIndex] = useState<number | null>(null);

    const handleOpenFrequentPeople = (index: number) => {
        setSelectedPersonIndex(index);
        setShowFrequentPeopleModal(true);
    };

    const handleSelectFrequentPerson = (name: string, email: string | null) => {
        if (selectedPersonIndex !== null) {
            const newPeople = [...splitPeople];
            newPeople[selectedPersonIndex].person_name = name;
            if (email) {
                newPeople[selectedPersonIndex].person_email = email;
            }
            setSplitPeople(newPeople);
        }
    };

    return (
        <div style={{
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            background: '#667eea',
            padding: '20px',
            minHeight: '100vh'
        }}>
            <div style={{
                width: 'min(720px, 100%)',
                margin: '0 auto',
                background: '#fff',
                borderRadius: '14px',
                padding: 'clamp(16px, 4vw, 28px)',
                boxShadow: '0 4px 12px rgba(0,0,0,.08)'
            }}>
                {/* Header */}
                <div style={{ textAlign: 'center', marginBottom: '22px' }}>
                    <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: '#10b981',
                        display: 'grid',
                        placeItems: 'center',
                        margin: '0 auto 14px'
                    }}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" style={{ width: '30px', height: '30px', stroke: '#fff', strokeWidth: 2, fill: 'none' }}>
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                            <circle cx="9" cy="7" r="4"/>
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                        </svg>
                    </div>
                    <h1 style={{ fontSize: 'clamp(22px, 6vw, 30px)', margin: '0 0 6px', color: '#111827' }}>
                        Kto sa zúčastní rozdelenia?
                    </h1>
                    <p style={{ color: '#6b7280', fontSize: 'clamp(15px, 4vw, 17px)' }}>
                        Zadajte mená alebo prezývky ľudí, medzi ktorých chcete bloček rozdeliť. Môžete si nastaviť minimálne 1 ďalšieho človeka, a najviac 7 ľudí.
                        <br /><br />
                        Ak im vyplníte e-mailové adresy, môžete im na záver poslať ich podiel so zoznamom položiek do mailu. Ak je daná e-mailová adresa zaregistrovaná ako užívateľ SmartBloček, zobrazí sa podiel užívateľa medzi jeho bločkami aj s jeho prerozdelenými položkami.
                    </p>
                </div>

                {/* People List */}
                <div style={{ margin: '20px 0' }}>
                    {splitPeople.map((person, index) => (
                        <div key={index} style={{
                            background: '#f9fafb',
                            border: '1px solid #e5e7eb',
                            borderRadius: '12px',
                            padding: '16px',
                            marginBottom: '12px'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <span style={{ fontWeight: 600, color: '#111827' }}>
                                    Osoba {index + 1}
                                    {index === 0 && <span style={{ marginLeft: '8px', fontSize: '13px', color: '#9ca3af', fontWeight: 400 }}>(Ty)</span>}
                                </span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {index > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => handleOpenFrequentPeople(index)}
                                            style={{
                                                background: 'transparent',
                                                border: 'none',
                                                cursor: 'pointer',
                                                padding: '4px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                color: '#667eea'
                                            }}
                                            title="Vybrať zo zoznamu"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                                <circle cx="9" cy="7" r="4"></circle>
                                                <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                                                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                            </svg>
                                        </button>
                                    )}
                                    {splitPeople.length > 2 && index > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => setSplitPeople(splitPeople.filter((_, i) => i !== index))}
                                            style={{
                                                background: '#ef4444',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                padding: '4px 8px',
                                                fontSize: '12px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            ✕ Odstrániť
                                        </button>
                                    )}
                                </div>
                            </div>
                            <input
                                type="text"
                                placeholder="Meno osoby *"
                                value={person.person_name}
                                disabled={index === 0}
                                onChange={(e) => {
                                    const newPeople = [...splitPeople];
                                    newPeople[index].person_name = e.target.value;
                                    setSplitPeople(newPeople);
                                }}
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    fontSize: '16px',
                                    marginBottom: '8px',
                                    boxSizing: 'border-box',
                                    ...(index === 0 ? { background: '#f3f4f6', color: '#9ca3af', cursor: 'not-allowed' } : {})
                                }}
                            />
                            <input
                                type="email"
                                placeholder="Email (voliteľné)"
                                value={person.person_email || ''}
                                disabled={index === 0}
                                onChange={(e) => {
                                    const newPeople = [...splitPeople];
                                    newPeople[index].person_email = e.target.value;
                                    setSplitPeople(newPeople);
                                }}
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    fontSize: '16px',
                                    boxSizing: 'border-box',
                                    ...(index === 0 ? { background: '#f3f4f6', color: '#9ca3af', cursor: 'not-allowed' } : {})
                                }}
                            />
                        </div>
                    ))}
                </div>

                {/* Add Person Button */}
                {splitPeople.length < 8 && (
                    <button
                        type="button"
                        onClick={() => setSplitPeople([...splitPeople, {person_name: ''}])}
                        style={{
                            width: '100%',
                            padding: '12px',
                            background: '#f3f4f6',
                            color: '#374151',
                            border: '2px dashed #d1d5db',
                            borderRadius: '12px',
                            fontSize: '16px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            marginBottom: '20px'
                        }}
                    >
                        + Pridať osobu ({splitPeople.length}/8)
                    </button>
                )}

                {/* Status Display */}
                {isSplitProcessing && (
                    <div style={{
                        background: '#eef2ff',
                        border: '1px solid #667eea',
                        borderRadius: '12px',
                        padding: '16px',
                        marginBottom: '20px',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            color: '#667eea',
                            fontSize: '16px',
                            fontWeight: 600,
                            marginBottom: '8px'
                        }}>
                            {splitStatusMessage}
                        </div>
                    </div>
                )}

                {/* Error Display */}
                {errorMessage && (
                    <div style={{
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '12px',
                        padding: '16px',
                        marginBottom: '20px',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            color: '#dc2626',
                            fontSize: '16px',
                            fontWeight: 600
                        }}>
                            ⚠️ {errorMessage}
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                <button
                    type="button"
                    onClick={onSavePeople}
                    disabled={isSplitProcessing}
                    style={{
                        width: '100%',
                        minHeight: '52px',
                        background: isSplitProcessing ? '#cbd5e1' : '#10b981',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '14px',
                        fontSize: '19px',
                        fontWeight: 700,
                        cursor: isSplitProcessing ? 'not-allowed' : 'pointer',
                        marginBottom: '12px'
                    }}
                >
                    {isSplitProcessing ? '⏳ Ukladám...' : '✅ Pokračovať na rozdelenie'}
                </button>

                <button
                    type="button"
                    onClick={onBackFromSplit}
                    style={{
                        width: '100%',
                        minHeight: '52px',
                        background: '#f3f4f6',
                        color: '#374151',
                        border: '2px solid #d1d5db',
                        borderRadius: '14px',
                        fontSize: '19px',
                        fontWeight: 600,
                        cursor: 'pointer'
                    }}
                >
                    ← Späť na bloček
                </button>

                {/* Footer */}
                <div style={{
                    textAlign: 'center',
                    marginTop: '18px',
                    color: '#9ca3af',
                    fontSize: '14px'
                }}>
                    V4 • 👥 Rozdelenie bločka • smartblocek.sk byTomasko
                </div>

                {/* Frequent People Modal */}
                <FrequentPeopleModal
                    isOpen={showFrequentPeopleModal}
                    onClose={() => setShowFrequentPeopleModal(false)}
                    onSelectPerson={handleSelectFrequentPerson}
                    getAuthToken={getAuthToken}
                />
            </div>
        </div>
    );
};

export default SplitPeopleStep;
